/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ReactNode } from 'react';

import { cssMap as customCssMap } from '@compiled/react';

import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

const gridStyles = cssMap({
	root: {
		display: 'grid',
		gridTemplateColumns: `
			[content-start] repeat(4, minmax(0, 1fr)) [content-end]
		`,
		rowGap: token('space.400'),
		columnGap: token('space.0'),

		width: '100%',
		maxWidth: '1220px',
		margin: '0 auto',

		paddingBlock: token('space.800'),
		paddingInline: token('space.200'),

		'@media (min-width: 48rem)': {
			paddingBlock: token('space.1000'),
			paddingInline: token('space.600'),
			gap: token('space.300'),
			gridTemplateColumns: `
				[content-start] repeat(12, minmax(0, 80px)) [content-end]
			`,
		},

		'@media (min-width: 64rem)': {
			rowGap: token('space.600'),
		},
	},
	compact: {
		paddingBlock: token('space.500'),
		'@media (min-width: 48rem)': {
			paddingBlock: token('space.800'),
		},
		'@media (min-width: 64rem)': {
			paddingBlock: token('space.1000'),
		},
	},
	none: {
		paddingBlock: token('space.0'),
		'@media (min-width: 48rem)': {
			paddingBlock: token('space.0'),
		},
		'@media (min-width: 64rem)': {
			paddingBlock: token('space.0'),
		},
	},
});

const hardcodedPaddingStyles = customCssMap({
	defaultLargePadding: {
		'@media (min-width: 64rem)': {
			paddingBlock: '160px',
		},
	},
});

interface GridContainerProps {
	children: ReactNode;
	as?: keyof JSX.IntrinsicElements;
	xcss?: any;
	spacing?: 'default' | 'compact' | 'none';
}

/**
 * __Grid Container__
 *
 * Shared grid container component for landing page sections.
 * Establishes a 12-column grid with named lines for consistent alignment.
 */
export const GridContainer: ({
	children,
	as,
	xcss,
	spacing,
}: GridContainerProps) => JSX.Element = ({
	children,
	as = 'div',
	xcss,
	spacing = 'default',
}: GridContainerProps) => {
	const Component = as;
	return (
		<Component
			css={[
				gridStyles.root,
				spacing === 'default' && hardcodedPaddingStyles.defaultLargePadding,
				spacing === 'compact' && gridStyles.compact,
				spacing === 'none' && gridStyles.none,
			]}
			className={xcss}
		>
			{children}
		</Component>
	);
};
